import React, { useState, useEffect, useCallback } from 'react';
import { getPlanHistory, setActivePlan, deletePlan, savePlan, duplicatePlan, renamePlan } from '../services/api';
import LessonPlanPreview from './LessonPlanPreview';
import '../styles/ModeHomeScreen.css';

function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="info-tooltip-wrap" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" className="info-tooltip-btn" onClick={e => { e.stopPropagation(); setOpen(v => !v); }} aria-label="More info">i</button>
      {open && <div className="info-tooltip-popup" role="tooltip">{text}</div>}
    </span>
  );
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function planSummary(plan) {
  const pj = plan.plan_json || {};
  if (plan.plan_type === 'testing') {
    const n = Array.isArray(pj.questions) ? pj.questions.length : '?';
    return `${n} question${n !== 1 ? 's' : ''}`;
  }
  if (plan.plan_type === 'cycles') {
    const n = Array.isArray(pj.cycles) ? pj.cycles.length : '?';
    return `${n} cycle${n !== 1 ? 's' : ''}`;
  }
  return '';
}

// ── Preview/Edit modal ────────────────────────────────────────────────────────

function PlanModal({ plan, paperName, user, paper, onClose, onSaved }) {
  const [editedPlan, setEditedPlan] = useState(plan.plan_json);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleEditSection = (index, updatedSection) => {
    const key = editedPlan.plan_type === 'testing' ? 'questions' : 'cycles';
    setEditedPlan(prev => ({ ...prev, [key]: prev[key].map((s, i) => i === index ? updatedSection : s) }));
    setSaved(false);
  };

  const handleReorderSection = (fromIndex, toIndex) => {
    setEditedPlan(prev => {
      const questions = [...prev.questions];
      const [moved] = questions.splice(fromIndex, 1);
      questions.splice(toIndex, 0, moved);
      return { ...prev, questions: questions.map((q, i) => ({ ...q, number: i + 1 })) };
    });
    setSaved(false);
  };

  const handleSaveEdits = async () => {
    setSaving(true);
    try {
      await savePlan(user.id, paper.id, editedPlan, null, '', plan.id);
      setSaved(true);
      onSaved?.();
    } catch {
      alert('Failed to save edits.');
    } finally {
      setSaving(false);
    }
  };

  const bankTakes = editedPlan.is_bank && editedPlan.bank_size && editedPlan.sample_size
    ? Math.floor(editedPlan.bank_size / editedPlan.sample_size)
    : null;
  const newBlocked = bankTakes !== null && bankTakes < 2;

  return (
    <div className="mhs-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mhs-modal">
        <div className="mhs-modal-header">
          <span className="mhs-modal-title">
            {plan.name || (plan.plan_type === 'testing' ? 'Assessment Plan' : 'Reading Cycle Plan')}
            {' · '}
            {formatDate(plan.created_at)}
          </span>
          <div className="mhs-modal-header-actions">
            <button
              className={`mhs-modal-save${saved ? ' mhs-modal-save--saved' : ''}`}
              onClick={handleSaveEdits}
              disabled={saving}
            >
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save edits'}
            </button>
            <button className="mhs-modal-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>
        <div className="mhs-modal-body">
          <LessonPlanPreview
            plan={editedPlan}
            paperName={paperName}
            onEditSection={handleEditSection}
            onReorderSection={handleReorderSection}
            readOnly={false}
            onApprove={() => {}}
            onRequestChanges={() => {}}
            approvalNotes=""
            onApprovalNotesChange={() => {}}
            hideApproval
          />
          {editedPlan.plan_type === 'testing' && (
            <div className="mhs-retake-section">
              {editedPlan.is_bank && (
                <div className="mhs-per-student-row">
                  <span className="mhs-per-student-label">Questions per student</span>
                  <input
                    type="number"
                    min={1}
                    max={Array.isArray(editedPlan.questions) ? editedPlan.questions.length : 100}
                    value={editedPlan.sample_size ?? 5}
                    className="mhs-per-student-input"
                    onChange={e => {
                      const total = Array.isArray(editedPlan.questions) ? editedPlan.questions.length : 100;
                      const val = Math.min(Math.max(1, parseInt(e.target.value, 10) || 1), total);
                      setEditedPlan(prev => ({ ...prev, sample_size: val }));
                      setSaved(false);
                    }}
                  />
                  <span className="mhs-per-student-of">of {Array.isArray(editedPlan.questions) ? editedPlan.questions.length : '?'} total</span>
                </div>
              )}
              <div className="retake-policy-header">
                <InfoTooltip text="Controls whether students can retake this test, and whether they receive the same questions or a new set from the question bank." />
                <span className="retake-policy-title">Retakes</span>
              </div>
              <div className="retake-policy-options">
                {(editedPlan.is_bank ? [
                  { val: 'none', label: 'No retakes' },
                  { val: 'same', label: 'Same questions' },
                  { val: 'new', label: 'All new questions' },
                  { val: 'random', label: 'Random questions' },
                ] : [
                  { val: 'none', label: 'No retakes' },
                  { val: 'same', label: 'Retakes allowed' },
                ]).map(({ val, label }) => {
                  const isDisabled = val === 'new' && newBlocked;
                  return (
                    <label key={val} className={`retake-policy-option${isDisabled ? ' retake-policy-option--disabled' : ''}`}>
                      <input
                        type="radio"
                        name="retake-policy-mhs"
                        value={val}
                        checked={(editedPlan.retake_policy || 'none') === val}
                        onChange={() => { if (!isDisabled) { setEditedPlan(prev => ({ ...prev, retake_policy: val })); setSaved(false); } }}
                        disabled={isDisabled}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
              {editedPlan.retake_policy === 'new' && bankTakes !== null && bankTakes >= 2 && (
                <span className="retake-new-hint">Students can take this test up to {bankTakes}× total</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const MODE_META = {
  cycle: {
    planType: 'cycles',
    icon: '📖',
    label: 'Cycle Mode',
    createLabel: '+ Create New Plan',
    emptyText: 'No cycle plans yet. Create one to get started.',
    accentClass: 'mhs--cycle',
  },
  test: {
    planType: 'testing',
    icon: '📝',
    label: 'Test Mode',
    createLabel: '+ Create New Plan',
    emptyText: 'No test plans yet. Create one to get started.',
    accentClass: 'mhs--test',
  },
};

export default function ModeHomeScreen({ mode, paper, user, onBack, onCreateNew, onPlanActivated }) {
  const meta = MODE_META[mode];
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actioning, setActioning] = useState(null);
  const [previewPlan, setPreviewPlan] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const loadPlans = useCallback(async () => {
    if (!paper?.id || !user?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await getPlanHistory(paper.id, user.id);
      setPlans((data.plans || []).filter(p => p.plan_type === meta.planType));
    } catch {
      setError('Failed to load plans. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [paper?.id, user?.id, meta.planType]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const handleSetActive = async (planId) => {
    setActioning(planId);
    try {
      await setActivePlan(planId, user.id, paper.id);
      setPlans(prev => prev.map(p => ({ ...p, is_active: p.id === planId })));
      onPlanActivated?.(meta.planType);
    } catch {
      alert('Failed to set active plan.');
    } finally {
      setActioning(null);
    }
  };

  const handleDuplicate = async (planId) => {
    setActioning(planId);
    try {
      const newPlan = await duplicatePlan(planId, user.id);
      if (newPlan.plan_type === meta.planType) {
        setPlans(prev => [newPlan, ...prev]);
      }
    } catch {
      alert('Failed to duplicate plan.');
    } finally {
      setActioning(null);
    }
  };

  const handleRenameSubmit = async (planId) => {
    try {
      await renamePlan(planId, user.id, renameValue);
      setPlans(prev => prev.map(p => p.id === planId ? { ...p, name: renameValue } : p));
    } catch {
      alert('Failed to rename plan.');
    } finally {
      setRenamingId(null);
      setRenameValue('');
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Delete this plan? This cannot be undone.')) return;
    setActioning(planId);
    try {
      await deletePlan(planId, user.id);
      setPlans(prev => prev.filter(p => p.id !== planId));
    } catch {
      alert('Failed to delete plan.');
    } finally {
      setActioning(null);
    }
  };

  return (
    <div className={`mhs ${meta.accentClass}`}>
      <div className="mhs-heading">
        <span className="mhs-heading-icon">{meta.icon}</span>
        <div>
          <h2 className="mhs-title">{meta.label} Plans</h2>
          <p className="mhs-subtitle">{paper?.name || 'this paper'}</p>
        </div>
      </div>

      <div className="mhs-list">
        <button className="mhs-create-btn mhs-create-btn--full" onClick={onCreateNew}>
          {meta.createLabel}
        </button>
        {loading && <p className="mhs-state-msg">Loading…</p>}
        {error && <p className="mhs-state-msg mhs-state-msg--error">{error}</p>}
        {!loading && !error && plans.length === 0 && (
          <p className="mhs-state-msg">{meta.emptyText}</p>
        )}
        {!loading && !error && plans.map(plan => (
          <div key={plan.id} className={`mhs-row${plan.is_active ? ' mhs-row--active' : ''}`}>
            <div className="mhs-row-top">
              <div className="mhs-row-meta">
                <span className="mhs-summary">{planSummary(plan)}</span>
                <span className="mhs-date">{formatDate(plan.created_at)}</span>
                {plan.is_active && <span className="mhs-active-badge">Active</span>}
              </div>

              {renamingId === plan.id ? (
                <div className="mhs-rename-row">
                  <input
                    className="mhs-rename-input"
                    autoFocus
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRenameSubmit(plan.id);
                      if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                    }}
                    placeholder="Enter a name…"
                  />
                  <button className="mhs-btn mhs-btn--save" onClick={() => handleRenameSubmit(plan.id)}>Save</button>
                  <button className="mhs-btn" onClick={() => { setRenamingId(null); setRenameValue(''); }}>Cancel</button>
                </div>
              ) : (
                <div
                  className="mhs-plan-name"
                  onClick={() => { setRenamingId(plan.id); setRenameValue(plan.name || ''); }}
                  title="Click to rename"
                >
                  {plan.name
                    ? <span className="mhs-name-text">{plan.name}</span>
                    : <span className="mhs-name-placeholder">+ Add name</span>
                  }
                </div>
              )}
            </div>

            <div className="mhs-row-actions">
              <button
                className="mhs-btn mhs-btn--preview"
                onClick={() => setPreviewPlan(plan)}
              >
                Preview &amp; Edit
              </button>
              <button
                className="mhs-btn mhs-btn--duplicate"
                disabled={actioning === plan.id}
                onClick={() => handleDuplicate(plan.id)}
              >
                {actioning === plan.id ? '…' : 'Duplicate'}
              </button>
              {!plan.is_active && (
                <button
                  className="mhs-btn mhs-btn--activate"
                  disabled={actioning === plan.id}
                  onClick={() => handleSetActive(plan.id)}
                >
                  Set Active
                </button>
              )}
              <button
                className="mhs-btn mhs-btn--delete"
                disabled={actioning === plan.id}
                onClick={() => handleDelete(plan.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {previewPlan && (
        <PlanModal
          plan={previewPlan}
          paperName={paper?.name}
          user={user}
          paper={paper}
          onClose={() => setPreviewPlan(null)}
          onSaved={loadPlans}
        />
      )}
    </div>
  );
}
